import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { money, formatDate, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { viewDoc, downloadDoc } from "../components/DocActions";
import {
  ShieldCheck, Plus, Sparkles, Loader2, Send, CalendarDays, ListChecks, X,
  AlertTriangle, Clock, HelpCircle, Trash2, CheckCircle2, Upload, Eye, Download,
  ExternalLink, Archive, FileText, MessageSquare, RefreshCw, Building2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";

const STATUSES = ["Active", "Due Soon", "Overdue", "Completed", "Not Applicable", "Needs Review"];
const REQ_TYPES = ["Business License", "Tax Filing", "Inspection", "Registration", "Certificate", "Disclosure", "Recertification", "Corporate Filing", "Permit", "Other"];
const FREQS = ["Annual", "Biennial", "Quarterly", "Monthly", "One-time", "Per Lease", "As Needed"];
const OFFSETS = [90, 60, 30, 15, 7, 1, 0];

const statusTone = {
  Active: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  "Due Soon": "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  Overdue: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  Completed: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  "Not Applicable": "bg-muted text-muted-foreground",
  "Needs Review": "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300",
};

function Sel({ testid, value, onChange, options, disabled }) {
  return (
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} disabled={disabled}
      className="w-full rounded-lg border border-border bg-background p-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

export default function Compliance() {
  const { user } = useAuth();
  const canWrite = ["admin", "manager"].includes(user?.role);
  const [sp, setSp] = useSearchParams();
  const propParam = sp.get("property_id") || "";
  const [tab, setTab] = useState("list");
  const [rows, setRows] = useState([]);
  const [cal, setCal] = useState([]);
  const [stats, setStats] = useState(null);
  const [props, setProps] = useState([]);
  const [filters, setFilters] = useState({ property_id: propParam, city: "", status: "", requirement_type: "" });
  const [detailId, setDetailId] = useState(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => { api.get("/properties").then((r) => setProps(r.data)).catch(() => {}); }, []);

  const qs = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    return p.toString() ? `?${p.toString()}` : "";
  }, [filters]);

  const load = useCallback(() => {
    api.get(`/compliance${qs()}`).then((r) => setRows(r.data)).catch(() => {});
    api.get("/compliance/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/compliance/calendar").then((r) => setCal(r.data)).catch(() => {});
  }, [qs]);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v || "" }));

  const runReminders = async () => {
    if (!window.confirm("Send due compliance reminder emails now (to the connected Gmail owner address)?")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/compliance/reminders/run");
      if (data.sent > 0) toast.success(`Sent ${data.sent} compliance reminder(s).`);
      else if (data.failed > 0) toast.error(`${data.failed} reminder(s) failed. Is Gmail connected?`);
      else toast.info("No compliance reminders due right now.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const propName = propParam ? (props.find((p) => p.id === propParam)?.name || "Property") : "";
  const statCards = stats ? [
    { k: "overdue", label: "Overdue", v: stats.overdue, icon: AlertTriangle, tone: "text-red-600", s: "Overdue" },
    { k: "due_soon", label: "Due Soon (60d)", v: stats.due_soon, icon: Clock, tone: "text-amber-600", s: "Due Soon" },
    { k: "needs_review", label: "Needs Review", v: stats.needs_review, icon: HelpCircle, tone: "text-violet-600", s: "Needs Review" },
    { k: "upcoming_60", label: "Upcoming (60d)", v: stats.upcoming_60, icon: CalendarDays, tone: "text-blue-600", s: "" },
  ] : [];

  return (
    <div className="space-y-6" data-testid="page-compliance">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Regulatory</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><ShieldCheck className="w-7 h-7 text-primary" /> Compliance{propName ? ` — ${propName}` : ""}</h1>
          <p className="text-sm text-muted-foreground mt-1">Track licenses, filings, inspections & renewals across city, county, state and property requirements.</p>
        </div>
        {canWrite && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="rounded-xl" onClick={runReminders} disabled={running} data-testid="run-reminders-btn">{running ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Send due reminders</Button>
            <Button variant="outline" className="rounded-xl" onClick={() => setSuggestOpen(true)} data-testid="suggest-btn"><Sparkles className="w-4 h-4 mr-1.5" /> Suggest Requirements</Button>
            <Button className="rounded-xl" onClick={() => setNewOpen(true)} data-testid="new-compliance-btn"><Plus className="w-4 h-4 mr-1.5" /> New Item</Button>
          </div>
        )}
      </div>

      {/* stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((c) => (
          <button key={c.k} onClick={() => { setTab("list"); setF("status", c.s); }} data-testid={`compliance-stat-${c.k}`}
            className="text-left rounded-2xl border border-border bg-card p-4 hover:shadow-md hover:border-primary/40 transition-all">
            <div className="flex items-center gap-2 mb-1"><c.icon className={`w-4.5 h-4.5 ${c.tone}`} /><span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{c.label}</span></div>
            <p className={`font-heading text-2xl font-bold ${c.tone}`}>{c.v}</p>
          </button>
        ))}
      </div>

      {/* tabs */}
      <div className="flex gap-2 border-b border-border">
        {[["list", "List", ListChecks], ["calendar", "Calendar", CalendarDays]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <Icon className="w-4 h-4" /> {l}
          </button>
        ))}
      </div>

      {tab === "list" ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Sel testid="filter-property" value={filters.property_id} onChange={(v) => { setF("property_id", v); v ? setSp({ property_id: v }) : setSp({}); }} options={[["", "All properties"], ...props.map((p) => [p.id, p.name])]} />
            <Sel testid="filter-status" value={filters.status} onChange={(v) => setF("status", v)} options={[["", "All statuses"], ...STATUSES.map((s) => [s, s])]} />
            <Sel testid="filter-type" value={filters.requirement_type} onChange={(v) => setF("requirement_type", v)} options={[["", "All types"], ...REQ_TYPES.map((t) => [t, t])]} />
            <input placeholder="Filter by city…" value={filters.city} onChange={(e) => setF("city", e.target.value)} data-testid="filter-city" className="w-full rounded-lg border border-border bg-background p-2 text-sm" />
          </div>

          {rows.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground">
              No compliance items yet. {canWrite && "Use \u201cSuggest Requirements\u201d to auto-detect what applies, or add one manually."}
            </div>
          ) : (
            <div className="rounded-2xl border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left font-semibold px-4 py-3">Requirement</th>
                    <th className="text-left font-semibold px-4 py-3">Property</th>
                    <th className="text-left font-semibold px-4 py-3">Type</th>
                    <th className="text-left font-semibold px-4 py-3">Next Due</th>
                    <th className="text-left font-semibold px-4 py-3">Frequency</th>
                    <th className="text-left font-semibold px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} onClick={() => setDetailId(r.id)} data-testid={`compliance-row-${r.id}`} className="border-t border-border hover:bg-muted/40 cursor-pointer transition-colors">
                      <td className="px-4 py-3 font-medium">{r.name}{r.auto_suggested && <Sparkles className="w-3 h-3 inline ml-1 text-primary" title="Auto-suggested" />}</td>
                      <td className="px-4 py-3 text-muted-foreground">{r.property_name}</td>
                      <td className="px-4 py-3 text-muted-foreground">{r.requirement_type}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{r.next_due_date ? formatDate(r.next_due_date) : "—"}{r.days_left != null && r.days_left >= 0 && r.days_left <= 90 && <span className="text-[11px] text-amber-600 ml-1">({r.days_left}d)</span>}</td>
                      <td className="px-4 py-3 text-muted-foreground">{r.frequency}</td>
                      <td className="px-4 py-3"><Badge className={`rounded-sm font-normal ${statusTone[r.computed_status] || "bg-muted"}`}>{r.computed_status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <CalendarView cal={cal} onOpen={setDetailId} />
      )}

      {detailId && <DetailDialog cid={detailId} canWrite={canWrite} onClose={() => { setDetailId(null); load(); }} />}
      {suggestOpen && <SuggestDialog props={props} defaultProp={filters.property_id} onClose={() => setSuggestOpen(false)} onApplied={() => { setSuggestOpen(false); load(); }} />}
      {newOpen && <NewItemDialog props={props} defaultProp={filters.property_id} onClose={() => setNewOpen(false)} onCreated={(id) => { setNewOpen(false); load(); setDetailId(id); }} />}
    </div>
  );
}

function CalendarView({ cal, onOpen }) {
  const groups = {};
  cal.forEach((c) => {
    const m = c.date.slice(0, 7);
    (groups[m] = groups[m] || []).push(c);
  });
  const months = Object.keys(groups).sort();
  if (months.length === 0) return <div data-testid="compliance-calendar-empty" className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground">No upcoming compliance deadlines.</div>;
  const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleString(undefined, { month: "long", year: "numeric" });
  return (
    <div className="space-y-5" data-testid="compliance-calendar">
      {months.map((m) => (
        <div key={m}>
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">{monthLabel(m)}</p>
          <div className="space-y-2">
            {groups[m].map((c) => (
              <button key={c.id} onClick={() => onOpen(c.id)} data-testid={`cal-item-${c.id}`} className="w-full text-left flex items-center gap-3 rounded-xl border border-border bg-card p-3 hover:border-primary/40 transition-colors">
                <div className="w-12 text-center shrink-0">
                  <p className="font-heading text-lg font-bold leading-none">{c.date.slice(8, 10)}</p>
                  <p className="text-[10px] uppercase text-muted-foreground">{new Date(`${c.date}T00:00:00`).toLocaleString(undefined, { month: "short" })}</p>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{c.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{c.property_name} · {c.requirement_type}</p>
                </div>
                <Badge className={`rounded-sm font-normal ${statusTone[c.status] || "bg-muted"}`}>{c.status}</Badge>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Fld({ label, children }) {
  return (<div><label className="text-xs font-medium text-muted-foreground mb-1 block">{label}</label>{children}</div>);
}

function SuggestDialog({ props, defaultProp, onClose, onApplied }) {
  const [pid, setPid] = useState(defaultProp || (props[0]?.id || ""));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState({});
  const [saving, setSaving] = useState(false);

  const run = async () => {
    if (!pid) { toast.error("Select a property"); return; }
    setLoading(true); setData(null);
    try {
      const { data } = await api.post("/compliance/suggest", { property_id: pid });
      setData(data);
      const s = {}; data.items.forEach((_, i) => { s[i] = true; }); setSel(s);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const apply = async () => {
    const items = (data?.items || []).filter((_, i) => sel[i]);
    if (items.length === 0) { toast.error("Select at least one item"); return; }
    setSaving(true);
    try {
      const { data: res } = await api.post("/compliance/suggest/apply", { property_id: pid, items });
      toast.success(`Added ${res.created} compliance item(s)`);
      onApplied();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="suggest-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Suggested Compliance Requirements</DialogTitle>
          <DialogDescription>A helpful starting point based on location & property profile. Review each item — nothing is added until you confirm.</DialogDescription>
        </DialogHeader>
        <div className="flex items-end gap-2">
          <div className="flex-1"><Fld label="Property"><Sel testid="suggest-property" value={pid} onChange={setPid} options={props.map((p) => [p.id, p.name])} /></Fld></div>
          <Button className="rounded-xl" onClick={run} disabled={loading} data-testid="suggest-run">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Detect"}</Button>
        </div>
        {data && (
          <>
            <p className="text-xs text-muted-foreground">{data.covered ? "Curated rules for this location." : "Location not in curated packs — AI-assisted suggestions (verify each)."} {data.property.city} {data.property.county} {data.property.state}</p>
            <div className="space-y-2">
              {data.items.length === 0 && <p className="text-sm text-muted-foreground">No suggestions found. Add items manually.</p>}
              {data.items.map((it, i) => (
                <label key={i} data-testid={`suggest-item-${i}`} className="flex gap-3 rounded-xl border border-border p-3 cursor-pointer hover:border-primary/40">
                  <input type="checkbox" checked={!!sel[i]} onChange={(e) => setSel((s) => ({ ...s, [i]: e.target.checked }))} className="mt-1" data-testid={`suggest-check-${i}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{it.name}</span>
                      <Badge className="rounded-sm font-normal bg-muted text-muted-foreground text-[11px]">{it.requirement_type}</Badge>
                      {it.needs_review && <Badge className="rounded-sm font-normal bg-violet-100 text-violet-800 dark:bg-violet-900/40 text-[11px]">Needs Review</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{it.agency}{it.frequency ? ` · ${it.frequency}` : ""}{it.next_due_date ? ` · due ${formatDate(it.next_due_date)}` : ""}</p>
                    {it.reason && <p className="text-xs text-muted-foreground mt-1"><strong>Why:</strong> {it.reason}</p>}
                    {it.source && <p className="text-[11px] text-muted-foreground/80 mt-0.5">Source: {it.source}</p>}
                    {it.applies_to?.length > 0 && <div className="flex gap-1 mt-1 flex-wrap">{it.applies_to.map((a) => <span key={a} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{a}</span>)}</div>}
                  </div>
                </label>
              ))}
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={onClose}>Cancel</Button>
          {data && data.items.length > 0 && <Button className="rounded-xl" onClick={apply} disabled={saving} data-testid="suggest-apply">{saving ? "Adding…" : "Add Selected"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewItemDialog({ props, defaultProp, onClose, onCreated }) {
  const [f, setF] = useState({ property_id: defaultProp || (props[0]?.id || ""), name: "", requirement_type: "Other", agency: "", frequency: "Annual", next_due_date: "", amount: "", website: "", status: "Active" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const create = async () => {
    if (!f.property_id || !f.name.trim()) { toast.error("Property and name are required"); return; }
    setSaving(true);
    try { const { data } = await api.post("/compliance", f); toast.success("Compliance item created"); onCreated(data.id); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="new-compliance-dialog">
        <DialogHeader><DialogTitle>New Compliance Item</DialogTitle><DialogDescription>Add a compliance requirement manually.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
          <Fld label="Property"><Sel testid="nc-property" value={f.property_id} onChange={(v) => set("property_id", v)} options={props.map((p) => [p.id, p.name])} /></Fld>
          <Fld label="Requirement Name"><input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="nc-name" className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Fld>
          <Fld label="Type"><Sel testid="nc-type" value={f.requirement_type} onChange={(v) => set("requirement_type", v)} options={REQ_TYPES.map((t) => [t, t])} /></Fld>
          <Fld label="Agency"><input value={f.agency} onChange={(e) => set("agency", e.target.value)} data-testid="nc-agency" className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Fld>
          <Fld label="Frequency"><Sel testid="nc-frequency" value={f.frequency} onChange={(v) => set("frequency", v)} options={FREQS.map((t) => [t, t])} /></Fld>
          <Fld label="Next Due Date"><input type="date" value={f.next_due_date} onChange={(e) => set("next_due_date", e.target.value)} data-testid="nc-due" className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Fld>
          <Fld label="Fee / Amount"><input value={f.amount} onChange={(e) => set("amount", e.target.value)} data-testid="nc-amount" className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Fld>
          <Fld label="Website / Payment Link"><input value={f.website} onChange={(e) => set("website", e.target.value)} data-testid="nc-website" className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Fld>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={onClose}>Cancel</Button>
          <Button className="rounded-xl" onClick={create} disabled={saving} data-testid="nc-save">{saving ? "Creating…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({ cid, canWrite, onClose }) {
  const [it, setIt] = useState(null);
  const [f, setF] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/compliance/${cid}`).then((r) => { setIt(r.data); setF(r.data); }).catch(() => toast.error("Could not load item"));
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleOffset = (o) => setF((s) => {
    const cur = new Set(s.reminder_offsets || []);
    cur.has(o) ? cur.delete(o) : cur.add(o);
    return { ...s, reminder_offsets: [...cur].sort((a, b) => b - a) };
  });

  const save = async () => {
    setBusy(true);
    try {
      const body = { name: f.name, requirement_type: f.requirement_type, agency: f.agency, next_due_date: f.next_due_date || "",
        last_completed_date: f.last_completed_date || "", frequency: f.frequency, status: f.status, amount: f.amount || "",
        website: f.website || "", notes: f.notes || "", reminder_offsets: f.reminder_offsets || [], overdue_reminder: !!f.overdue_reminder };
      const { data } = await api.put(`/compliance/${cid}`, body); setIt(data); setF(data); toast.success("Saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const complete = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/compliance/${cid}/complete`); setIt(data); setF(data); toast.success("Marked completed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    setBusy(true);
    try { const { data } = await api.put(`/compliance/${cid}`, { status }); setIt(data); setF(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const archive = async () => {
    setBusy(true);
    try { await api.put(`/compliance/${cid}`, { archived: true }); toast.success("Archived"); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this compliance item permanently?")) return;
    try { await api.delete(`/compliance/${cid}`); toast.success("Deleted"); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/compliance/${cid}/notes`, { text: note.trim() }); setIt(data); setNote(""); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const upload = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try { const { data } = await api.post(`/compliance/${cid}/attachments`, { name: file.name, file_type: file.name.split(".").pop().toLowerCase(), size_kb: Math.round(file.size / 1024), data: reader.result }); setIt(data); setF(data); toast.success("File uploaded"); }
      catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };
  const removeAtt = async (docId) => {
    try { const { data } = await api.delete(`/compliance/${cid}/attachments/${docId}`); setIt(data); setF(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const activity = [...(it?.activity || [])].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  const fmtDT = (iso) => { const d = new Date(iso); return isNaN(d) ? iso : d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="compliance-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" /> {it?.name || "Compliance Item"}</DialogTitle>
          <DialogDescription>Edit details, documents, reminders and history for this compliance requirement.</DialogDescription>
        </DialogHeader>
        {!it ? <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" /></div> : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={`rounded-sm font-normal ${statusTone[it.computed_status] || "bg-muted"}`} data-testid="detail-status-badge">{it.computed_status}</Badge>
              <span className="text-sm text-muted-foreground inline-flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {it.property_name} · {[it.city, it.county, it.state].filter(Boolean).join(", ")}</span>
              {canWrite && (
                <div className="ml-auto flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-lg" onClick={complete} disabled={busy} data-testid="detail-complete-btn"><CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark Completed</Button>
                  <Button size="sm" variant="outline" className="rounded-lg" onClick={() => setStatus("Not Applicable")} disabled={busy} data-testid="detail-na-btn">N/A</Button>
                </div>
              )}
            </div>

            {it.source && <div className="rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground"><strong>Suggested because:</strong> {it.reason || it.source}{it.last_verified && <> · Last verified {formatDate(it.last_verified)}</>}{it.applies_to?.length > 0 && <div className="flex gap-1 mt-1 flex-wrap">{it.applies_to.map((a) => <span key={a} className="px-1.5 py-0.5 rounded bg-background">{a}</span>)}</div>}</div>}

            {canWrite ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Fld label="Requirement Name"><input value={f.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="detail-name" className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Fld>
                <Fld label="Type"><Sel testid="detail-type" value={f.requirement_type} onChange={(v) => set("requirement_type", v)} options={REQ_TYPES.map((t) => [t, t])} /></Fld>
                <Fld label="Agency / Authority"><input value={f.agency || ""} onChange={(e) => set("agency", e.target.value)} data-testid="detail-agency" className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Fld>
                <Fld label="Status"><Sel testid="detail-status" value={f.status} onChange={(v) => set("status", v)} options={STATUSES.map((s) => [s, s])} /></Fld>
                <Fld label="Next Due Date"><input type="date" value={(f.next_due_date || "").slice(0, 10)} onChange={(e) => set("next_due_date", e.target.value)} data-testid="detail-due" className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Fld>
                <Fld label="Frequency"><Sel testid="detail-frequency" value={f.frequency} onChange={(v) => set("frequency", v)} options={FREQS.map((t) => [t, t])} /></Fld>
                <Fld label="Fee / Amount"><input value={f.amount || ""} onChange={(e) => set("amount", e.target.value)} data-testid="detail-amount" className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Fld>
                <Fld label="Last Completed"><input type="date" value={(f.last_completed_date || "").slice(0, 10)} onChange={(e) => set("last_completed_date", e.target.value)} data-testid="detail-last-completed" className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Fld>
                <div className="sm:col-span-2"><Fld label="Website / Payment Link"><input value={f.website || ""} onChange={(e) => set("website", e.target.value)} data-testid="detail-website" className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Fld></div>
                <div className="sm:col-span-2"><Fld label="Notes"><textarea value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="detail-notes" className="w-full rounded-lg border border-border bg-background p-2 text-sm resize-none" /></Fld></div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-[11px] uppercase text-muted-foreground font-semibold">Agency</p>{it.agency || "—"}</div>
                <div><p className="text-[11px] uppercase text-muted-foreground font-semibold">Next Due</p>{it.next_due_date ? formatDate(it.next_due_date) : "—"}</div>
                <div><p className="text-[11px] uppercase text-muted-foreground font-semibold">Frequency</p>{it.frequency}</div>
                <div><p className="text-[11px] uppercase text-muted-foreground font-semibold">Fee</p>{it.amount || "—"}</div>
              </div>
            )}

            {/* reminders */}
            {canWrite && (
              <div>
                <p className="text-sm font-semibold mb-2">Reminder settings (days before due)</p>
                <div className="flex flex-wrap gap-2">
                  {OFFSETS.map((o) => (
                    <button key={o} onClick={() => toggleOffset(o)} data-testid={`offset-${o}`}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${(f.reminder_offsets || []).includes(o) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary"}`}>
                      {o === 0 ? "On due date" : `${o}d`}
                    </button>
                  ))}
                  <button onClick={() => set("overdue_reminder", !f.overdue_reminder)} data-testid="offset-overdue"
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${f.overdue_reminder ? "bg-red-500 text-white border-red-500" : "border-border text-muted-foreground hover:border-red-400"}`}>Overdue</button>
                </div>
              </div>
            )}

            {/* website link visible */}
            {it.website && <a href={it.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline" data-testid="detail-website-link"><ExternalLink className="w-4 h-4" /> Agency / payment link</a>}

            {/* attachments */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold flex items-center gap-2"><FileText className="w-4 h-4 text-primary" /> Documents</p>
                {canWrite && (<label className="text-xs inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-dashed border-border cursor-pointer hover:border-primary hover:text-primary" data-testid="detail-upload-label"><Upload className="w-3.5 h-3.5" /> Upload<input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="detail-fileinput" /></label>)}
              </div>
              {(it.attachments || []).length === 0 ? <p className="text-sm text-muted-foreground">No documents uploaded.</p> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(it.attachments || []).map((a) => (
                    <div key={a.doc_id} data-testid={`attachment-${a.doc_id}`} className="flex items-center gap-2 rounded-lg border border-border p-2">
                      <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="text-sm truncate flex-1">{a.name}</span>
                      <button onClick={() => viewDoc(a.doc_id)} data-testid={`att-view-${a.doc_id}`} className="text-primary hover:opacity-70" title="View"><Eye className="w-4 h-4" /></button>
                      <button onClick={() => downloadDoc(a.doc_id)} className="text-muted-foreground hover:text-primary" title="Download"><Download className="w-4 h-4" /></button>
                      {canWrite && <button onClick={() => removeAtt(a.doc_id)} data-testid={`att-del-${a.doc_id}`} className="text-muted-foreground hover:text-destructive" title="Remove"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* activity */}
            <div>
              <p className="text-sm font-semibold flex items-center gap-2 mb-2"><Clock className="w-4 h-4 text-primary" /> Notes & History</p>
              {canWrite && (
                <div className="flex items-start gap-2 mb-3">
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add a note…" data-testid="detail-note-input" className="flex-1 rounded-lg border border-border bg-background p-2 text-sm resize-none" />
                  <Button className="rounded-lg" onClick={addNote} disabled={busy || !note.trim()} data-testid="detail-add-note-btn"><MessageSquare className="w-4 h-4" /></Button>
                </div>
              )}
              {activity.length === 0 ? <p className="text-sm text-muted-foreground">No activity yet.</p> : (
                <div className="space-y-2" data-testid="detail-activity">
                  {activity.map((a) => (
                    <div key={a.id} className="flex gap-3 rounded-lg border border-border p-2.5">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${a.type === "note" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>{a.type === "note" ? <MessageSquare className="w-3.5 h-3.5" /> : <RefreshCw className="w-3.5 h-3.5" />}</div>
                      <div className="min-w-0 flex-1"><p className="text-sm whitespace-pre-line">{a.text}</p><p className="text-[11px] text-muted-foreground mt-0.5">{a.user_name || a.user || "System"} · {fmtDT(a.at)}</p></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <DialogFooter className="flex-wrap gap-2">
          {canWrite && it && <><Button variant="outline" className="rounded-xl text-muted-foreground" onClick={archive} data-testid="detail-archive-btn"><Archive className="w-4 h-4 mr-1.5" /> Archive</Button>
          <Button variant="outline" className="rounded-xl text-red-600" onClick={remove} data-testid="detail-delete-btn"><Trash2 className="w-4 h-4 mr-1.5" /> Delete</Button>
          <Button className="rounded-xl" onClick={save} disabled={busy} data-testid="detail-save-btn">{busy ? "Saving…" : "Save Changes"}</Button></>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
